import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { StatusBadge, Badge } from '../common';
import { ROLE_META, STATUS_META } from '../../utils/constants';
import { calculateTotalEarnings } from '../../services/commissionService';
import { Icon } from '../../components/common/Icon';
import { AlertCircle } from 'lucide-react';
import { supabase } from '../../services/supabaseClient';

/**
 * Admin Dashboard (المدير التنفيذي)
 * Complete system overview: team performance, deal pipeline, financial summary
 */
export function AdminDashboard({ currentUser, restaurants, users, userCommissions, allCommissions }) {
  const navigate = useNavigate();
  const [pipelineStats, setPipelineStats] = useState({});
  const [teamStats, setTeamStats] = useState([]);

  // Calculate dashboard stats
  useEffect(() => {
    if (!restaurants || !users) return;

    // Pipeline stats
    const stats = {};
    Object.keys(STATUS_META).forEach(status => {
      stats[status] = restaurants.filter(r => r.status === status).length;
    });
    setPipelineStats(stats);

    // Team stats
    const teamData = users.map(user => {
      const userRestaurants = restaurants.filter(r => {
        if (user.role === 'marketer') return r.assignedTo === user.id;
        if (user.role === 'rep') return r.assignedRep === user.id;
        return false;
      });

      const closedDeals = userRestaurants.filter(r => r.status === 'deal_closed').length;
      const inProgress = userRestaurants.filter(r => r.status !== 'deal_closed').length;

      return {
        ...user,
        totalDeals: userRestaurants.length,
        closedDeals,
        inProgress,
      };
    });

    setTeamStats(teamData);
  }, [restaurants, users]);

  // Wrong info pending reviews
  const pendingWrongInfoReviews = useMemo(() => {
    return (restaurants || []).filter(r => r.wrongInfoFlag && r.wrongInfoStatus === 'pending');
  }, [restaurants]);

  // Users ready for calls
  const readyUsers = useMemo(() => {
    return (users || []).filter(u => u.is_ready_for_call && !u.is_approved_to_work);
  }, [users]);

  // Quiz submissions pending review
  const [pendingQuizzes, setPendingQuizzes] = useState([]);

  useEffect(() => {
    if (currentUser?.role !== 'admin') return;
    
    const loadPendingQuizzes = async () => {
      const { data } = await supabase
        .from('user_quiz_attempts')
        .select('*, users(name, role)')
        .eq('review_status', 'pending')
        .order('submitted_at', { ascending: false })
        .limit(10);
      
      setPendingQuizzes(data || []);
    };
    
    loadPendingQuizzes();
  }, [currentUser]);

  const approveQuiz = async (quizId, userId) => {
    try {
      await supabase
        .from('user_quiz_attempts')
        .update({
          review_status: 'approved',
          reviewed_by: currentUser.id,
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', quizId);

      await supabase
        .from('users')
        .update({ is_approved_to_work: true, approved_at: new Date().toISOString(), approved_by: currentUser.id })
        .eq('id', userId);

      window.location.reload();
    } catch (err) {
      console.error('Approve error:', err);
    }
  };

  const rejectQuiz = async (quizId) => {
    try {
      await supabase
        .from('user_quiz_attempts')
        .update({
          review_status: 'rejected',
          reviewed_by: currentUser.id,
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', quizId);

      await supabase
        .from('users')
        .update({ is_ready_for_call: false, quiz_passed: false })
        .eq('id', userId);

      setPendingQuizzes(prev => prev.filter(q => q.id !== quizId));
    } catch (err) {
      console.error('Reject error:', err);
    }
  };

  // System overview stats
  const totalRestaurants = restaurants?.length || 0;
  const totalClosed = restaurants?.filter(r => r.status === 'deal_closed').length || 0;
  const totalInProgress = totalRestaurants - totalClosed;
  const activeUsers = users?.filter(u => u.is_online).length || 0;

  // Financial stats
  const totalCommissions = calculateTotalEarnings(allCommissions || []);
  const pendingCommissions = (allCommissions || []).filter(c => c.status === 'pending').reduce((sum, c) => sum + parseFloat(c.amount || 0), 0);
  const paidCommissions = (allCommissions || []).filter(c => c.status === 'paid').reduce((sum, c) => sum + parseFloat(c.amount || 0), 0);

  return (
    <div style={{ padding: 'clamp(16px, 3vw, 32px)' }}>
      {/* Header */}
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ color: '#000000', fontSize: 28, fontWeight: 900, marginBottom: 8 }}>
          👑 لوحة تحكم المدير التنفيذي
        </h1>
        <p style={{ color: '#000000', fontSize: 14 }}>
          نظرة شاملة على أداء النظام والفريق
        </p>
      </div>

      {/* Wrong Info Reviews Container - Always Visible */}
      <div style={{
        background: pendingWrongInfoReviews.length > 0 ? 'linear-gradient(135deg, #ffffff, #ffffff)' : '#ffffff',
        border: pendingWrongInfoReviews.length > 0 ? '2px solid #f59e0b' : '1px solid #e2e8f0',
        borderRadius: 16,
        padding: 24,
        marginBottom: 32,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 48,
              height: 48,
              borderRadius: '50%',
              background: pendingWrongInfoReviews.length > 0 ? '#fef3c7' : '#e2e8f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <AlertCircle size={28} color={pendingWrongInfoReviews.length > 0 ? '#f59e0b' : '#64748b'} />
            </div>
            <div>
              <h2 style={{ 
                color: pendingWrongInfoReviews.length > 0 ? '#fcd34d' : '#f1f5f9', 
                fontSize: 18, 
                fontWeight: 900, 
                margin: '0 0 4px' 
              }}>
                {pendingWrongInfoReviews.length > 0 ? '⚠️ بلاغات بانتظار المراجعة' : '📋 مراجعة المعلومات الخاطئة'}
              </h2>
              <p style={{ color: pendingWrongInfoReviews.length > 0 ? '#fbbf24' : '#94a3b8', fontSize: 13, margin: 0 }}>
                {pendingWrongInfoReviews.length > 0 
                  ? `لديك ${pendingWrongInfoReviews.length} تقرير معلومات خاطئة يحتاج مراجعتك`
                  : 'لا توجد تقارير بانتظار المراجعة حالياً'}
              </p>
            </div>
          </div>
          {pendingWrongInfoReviews.length > 0 && (
            <button
              onClick={() => navigate(`/${currentUser.role}/wrong-info-review`)}
              style={{
                background: '#f59e0b',
                border: 'none',
                borderRadius: 10,
                padding: '10px 20px',
                color: '#fff',
                cursor: 'pointer',
                fontFamily: "'Cairo',sans-serif",
                fontSize: 14,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
              }}
            >
              مراجعة الكل
              <Icon name="ArrowLeft" size={16} color="#fff" />
            </button>
          )}
        </div>

        {/* List of up to 5 pending reviews */}
        {pendingWrongInfoReviews.length > 0 ? (
          <div style={{ display: 'grid', gap: 10 }}>
            {pendingWrongInfoReviews.slice(0, 5).map(report => {
              const userWhoFlagged = users?.find(u => u.id === report.wrongInfoFlaggedBy);
              const typeLabels = {
                'wrong_number': 'رقم هاتف خاطئ',
                'wrong_name': 'اسم المطعم خاطئ',
                'wrong_location': 'موقع خاطئ',
                'duplicate': 'مكرر',
                'closed': 'المطعم مغلق',
                'other': 'أخرى',
              };

              return (
                <div
                  key={report.id}
                  onClick={() => navigate(`/${currentUser.role}/wrong-info-review`)}
                  style={{
                    background: '#ffffff',
                    borderRadius: 10,
                    padding: 14,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    border: '1px solid #f59e0b44',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = '#e2e8f0';
                    e.currentTarget.style.borderColor = '#f59e0b';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = '#ffffff';
                    e.currentTarget.style.borderColor = '#f59e0b44';
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
                    <span style={{ fontSize: 20 }}>🍽️</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: '#000000', fontSize: 14, fontWeight: 700 }}>
                        {report.name}
                      </div>
                      <div style={{ color: '#000000', fontSize: 12 }}>
                        {userWhoFlagged?.name || 'مستخدم'} • {typeLabels[report.wrongInfoType] || report.wrongInfoType}
                        {report.wrongInfoDescription && ` • ${report.wrongInfoDescription.substring(0, 50)}${report.wrongInfoDescription.length > 50 ? '...' : ''}`}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{
                      background: '#f59e0b22',
                      color: '#f59e0b',
                      padding: '4px 10px',
                      borderRadius: 6,
                      fontSize: 11,
                      fontWeight: 700,
                      whiteSpace: 'nowrap',
                    }}>
                      {new Date(report.wrongInfoFlaggedAt).toLocaleDateString('ar-AE', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                    </span>
                    <Icon name="ArrowLeft" size={16} color="#f59e0b" />
                  </div>
                </div>
              );
            })}
            {pendingWrongInfoReviews.length > 5 && (
              <div style={{ color: '#fbbf24', fontSize: 13, textAlign: 'center', padding: 8, fontWeight: 700 }}>
                +{pendingWrongInfoReviews.length - 5} تقارير أخرى - اضغط "مراجعة الكل" لعرض الكل
              </div>
            )}
          </div>
        ) : (
          <div style={{
            textAlign: 'center',
            padding: '40px 20px',
            color: '#64748b',
          }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>✅</div>
            <p style={{ fontSize: 14, fontWeight: 700, marginBottom: 4 }}>لا توجد بلاغات حالياً</p>
            <p style={{ fontSize: 12 }}>عندما يقوم المستخدمون بالإبلاغ عن معلومات خاطئة، ستظهر هنا</p>
          </div>
        )}
      </div>

      {/* Ready Users Notification */}
      {readyUsers.length > 0 && (
        <div style={{
          background: 'linear-gradient(135deg, #ffffff, #ffffff)',
          border: '2px solid #10b981',
          borderRadius: 16,
          padding: 24,
          marginBottom: 32,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                background: '#10b98122',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <span style={{ fontSize: 28 }}>🎉</span>
              </div>
              <div>
                <h3 style={{ color: '#000000', fontSize: 18, fontWeight: 900, margin: 0 }}>
                  مستخدمون جاهزون للمكالمات
                </h3>
                <p style={{ color: '#6ee7b7', fontSize: 13, margin: '4px 0 0' }}>
                  {readyUsers.length} مستخدم أكمل التدريب وجاهز للعمل
                </p>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {readyUsers.slice(0, 5).map(user => (
              <div
                key={user.id}
                style={{
                  background: '#ffffff',
                  borderRadius: 10,
                  padding: 14,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  border: '1px solid #10b98144',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div style={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    background: '#10b98122',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 18,
                    fontWeight: 900,
                    color: '#10b981',
                  }}>
                    {user.name.charAt(0)}
                  </div>
                  <div>
                    <div style={{ color: '#000000', fontSize: 14, fontWeight: 700 }}>
                      {user.name}
                    </div>
                    <div style={{ color: '#000000', fontSize: 12 }}>
                      {user.role === 'marketer' ? 'منسقة صفقات' : user.role === 'rep' ? 'مندوب' : user.role} • 
                      تقدم التعلم: {user.learning_progress || 0}%
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => {
                    // Approve user
                    import('../../services/userService').then(({ approveUser }) => {
                      approveUser(user.id, currentUser.id).then(() => {
                        window.location.reload();
                      });
                    });
                  }}
                  style={{
                    padding: '8px 16px',
                    background: 'linear-gradient(135deg, #10b981, #059669)',
                    border: 'none',
                    borderRadius: 8,
                    color: '#fff',
                    fontSize: 13,
                    fontWeight: 700,
                    cursor: 'pointer',
                    fontFamily: "'Cairo', sans-serif",
                  }}
                >
                  ✅ اعتماد
                </button>
              </div>
            ))}
            {readyUsers.length > 5 && (
              <div style={{ color: '#6ee7b7', fontSize: 13, textAlign: 'center', padding: 8, fontWeight: 700 }}>
                +{readyUsers.length - 5} مستخدمين آخرين - اعتمد من الإعدادات
              </div>
            )}
          </div>
        </div>
      )}

      {/* Quiz Review Section */}
      {pendingQuizzes.length > 0 && (
        <div style={{
          background: 'linear-gradient(135deg, #ffffff, #312e81)',
          border: '2px solid #8b5cf6',
          borderRadius: 16,
          padding: 24,
          marginBottom: 32,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                background: '#8b5cf622',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
                <span style={{ fontSize: 28 }}>📝</span>
              </div>
              <div>
                <h3 style={{ color: '#000000', fontSize: 18, fontWeight: 900, margin: 0 }}>
                  اختبارات بانتظار المراجعة
                </h3>
                <p style={{ color: '#a78bfa', fontSize: 13, margin: '4px 0 0' }}>
                  {pendingQuizzes.length} مستخدم اجتاز الاختبار ويحتاج اعتماد
                </p>
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {pendingQuizzes.map(quiz => (
              <div
                key={quiz.id}
                style={{
                  background: '#ffffff',
                  borderRadius: 10,
                  padding: 16,
                  border: '1px solid #8b5cf644',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: '#8b5cf622',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 18,
                      fontWeight: 900,
                      color: '#8b5cf6',
                    }}>
                      {quiz.users?.name?.charAt(0) || '?'}
                    </div>
                    <div>
                      <div style={{ color: '#000000', fontSize: 14, fontWeight: 700 }}>
                        {quiz.users?.name || 'مستخدم'}
                      </div>
                      <div style={{ color: '#000000', fontSize: 12 }}>
                        النتيجة: <strong style={{ color: '#10b981' }}>{quiz.score_percentage}%</strong> • 
                        {quiz.correct_answers}/{quiz.total_questions} صحيح • 
                        {new Date(quiz.submitted_at).toLocaleDateString('ar-AE')}
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <button
                      onClick={() => approveQuiz(quiz.id, quiz.user_id)}
                      style={{
                        padding: '8px 16px',
                        background: 'linear-gradient(135deg, #10b981, #059669)',
                        border: 'none',
                        borderRadius: 8,
                        color: '#fff',
                        fontSize: 13,
                        fontWeight: 700,
                        cursor: 'pointer',
                        fontFamily: "'Cairo', sans-serif",
                      }}
                    >
                      ✅ اعتماد
                    </button>
                    <button
                      onClick={() => rejectQuiz(quiz.id)}
                      style={{
                        padding: '8px 16px',
                        background: '#ef444422',
                        border: '1px solid #ef444444',
                        borderRadius: 8,
                        color: '#ef4444',
                        fontSize: 13,
                        fontWeight: 700,
                        cursor: 'pointer',
                        fontFamily: "'Cairo', sans-serif",
                      }}
                    >
                      ❌ رفض
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* System Overview Cards */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', 
        gap: 16, 
        marginBottom: 32 
      }}>
        <div style={{
          background: 'linear-gradient(135deg, #ffffff, #ffffff)',
          border: '1px solid #3b82f6',
          borderRadius: 16,
          padding: 24,
        }}>
          <p style={{ color: '#93c5fd', fontSize: 12, marginBottom: 8 }}>إجمالي المطاعم</p>
          <p style={{ color: '#3b82f6', fontSize: 36, fontWeight: 900 }}>{totalRestaurants}</p>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #ffffff, #ffffff)',
          border: '1px solid #10b981',
          borderRadius: 16,
          padding: 24,
        }}>
          <p style={{ color: '#6ee7b7', fontSize: 12, marginBottom: 8 }}>صفقات مغلقة</p>
          <p style={{ color: '#34d399', fontSize: 36, fontWeight: 900 }}>{totalClosed}</p>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #ffffff, #ffffff)',
          border: '1px solid #f59e0b',
          borderRadius: 16,
          padding: 24,
        }}>
          <p style={{ color: '#fcd34d', fontSize: 12, marginBottom: 8 }}>قيد العمل</p>
          <p style={{ color: '#f59e0b', fontSize: 36, fontWeight: 900 }}>{totalInProgress}</p>
        </div>

        <div style={{
          background: 'linear-gradient(135deg, #ffffff, #ffffff)',
          border: '1px solid #22d3ee',
          borderRadius: 16,
          padding: 24,
        }}>
          <p style={{ color: '#67e8f9', fontSize: 12, marginBottom: 8 }}>مستخدمين نشطين</p>
          <p style={{ color: '#22d3ee', fontSize: 36, fontWeight: 900 }}>{activeUsers}</p>
        </div>
      </div>

      {/* Pipeline Overview */}
      <div style={{
        background: '#ffffff',
        borderRadius: 16,
        border: '1px solid #e2e8f0',
        padding: 24,
        marginBottom: 24,
      }}>
        <h2 style={{ color: '#000000', fontSize: 18, fontWeight: 700, margin: '0 0 20px' }}>
          📊 مسار العمل
        </h2>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12 }}>
          {Object.entries(STATUS_META).map(([status, meta]) => (
            <div key={status} style={{
              background: meta.bg || '#ffffff',
              border: `1px solid ${meta.color}44`,
              borderRadius: 12,
              padding: 16,
              textAlign: 'center',
            }}>
              <div style={{ marginBottom: 8, color: meta.color }}>
                <Icon name={meta.icon} size={24} color={meta.color} />
              </div>
              <div style={{ color: meta.color, fontSize: 28, fontWeight: 900, marginBottom: 4 }}>
                {pipelineStats[status] || 0}
              </div>
              <div style={{ color: '#000000', fontSize: 11 }}>{meta.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Team Performance */}
      <div style={{
        background: '#ffffff',
        borderRadius: 16,
        border: '1px solid #e2e8f0',
        overflow: 'hidden',
        marginBottom: 24,
      }}>
        <div style={{ padding: 20, borderBottom: '1px solid #e2e8f0' }}>
          <h2 style={{ color: '#000000', fontSize: 18, fontWeight: 700, margin: 0 }}>
            👥 أداء الفريق
          </h2>
        </div>

        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: '#ffffff' }}>
                <th style={{ padding: '12px 16px', color: '#3d5470', fontSize: 11, fontWeight: 700, textAlign: 'right' }}>المستخدم</th>
                <th style={{ padding: '12px 16px', color: '#3d5470', fontSize: 11, fontWeight: 700, textAlign: 'right' }}>الدور</th>
                <th style={{ padding: '12px 16px', color: '#3d5470', fontSize: 11, fontWeight: 700, textAlign: 'right' }}>إجمالي الصفقات</th>
                <th style={{ padding: '12px 16px', color: '#3d5470', fontSize: 11, fontWeight: 700, textAlign: 'right' }}>مغلقة</th>
                <th style={{ padding: '12px 16px', color: '#3d5470', fontSize: 11, fontWeight: 700, textAlign: 'right' }}>قيد العمل</th>
                <th style={{ padding: '12px 16px', color: '#3d5470', fontSize: 11, fontWeight: 700, textAlign: 'right' }}>الحالة</th>
              </tr>
            </thead>
            <tbody>
              {teamStats.map(user => {
                const rm = ROLE_META[user.role] || { label: user.role, color: '#64748b', icon: '👤' };
                return (
                  <tr key={user.id} style={{ borderTop: '1px solid #e2e8f0' }}>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ color: '#000000', fontWeight: 700 }}>{user.name}</div>
                      <div style={{ color: '#64748b', fontSize: 11 }}>{user.email}</div>
                    </td>
                    <td style={{ padding: '12px 16px' }}>
                      <span style={{
                        background: rm.color + '22',
                        color: rm.color,
                        padding: '4px 10px',
                        borderRadius: 20,
                        fontSize: 11,
                        fontWeight: 700,
                      }}>
                        <Icon name={rm.icon} size={12} color={rm.color} />
                        <span>{rm.label}</span>
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', color: '#000000', fontWeight: 700 }}>{user.totalDeals}</td>
                    <td style={{ padding: '12px 16px', color: '#10b981', fontWeight: 700 }}>{user.closedDeals}</td>
                    <td style={{ padding: '12px 16px', color: '#f59e0b', fontWeight: 700 }}>{user.inProgress}</td>
                    <td style={{ padding: '12px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{
                          width: 8, height: 8, borderRadius: '50%',
                          background: user.is_online ? '#10b981' : '#64748b',
                          boxShadow: user.is_online ? '0 0 8px #10b98188' : 'none',
                          display: 'inline-block',
                        }} />
                        <span style={{ color: user.is_online ? '#10b981' : '#64748b', fontSize: 12 }}>
                          {user.is_online ? 'متصل' : 'غير متصل'}
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Financial Summary */}
      <div style={{
        background: '#ffffff',
        borderRadius: 16,
        border: '1px solid #e2e8f0',
        padding: 24,
      }}>
        <h2 style={{ color: '#000000', fontSize: 18, fontWeight: 700, margin: '0 0 20px' }}>
          💰 الملخص المالي
        </h2>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16 }}>
          <div style={{
            background: '#ffffff',
            border: '1px solid #10b981',
            borderRadius: 12,
            padding: 20,
          }}>
            <p style={{ color: '#6ee7b7', fontSize: 12, marginBottom: 8 }}>إجمالي العمولات</p>
            <p style={{ color: '#34d399', fontSize: 28, fontWeight: 900 }}>{totalCommissions.toFixed(0)} AED</p>
          </div>

          <div style={{
            background: '#ffffff',
            border: '1px solid #f59e0b',
            borderRadius: 12,
            padding: 20,
          }}>
            <p style={{ color: '#fcd34d', fontSize: 12, marginBottom: 8 }}>في الانتظار</p>
            <p style={{ color: '#f59e0b', fontSize: 28, fontWeight: 900 }}>{pendingCommissions.toFixed(0)} AED</p>
          </div>

          <div style={{
            background: '#ffffff',
            border: '1px solid #3b82f6',
            borderRadius: 12,
            padding: 20,
          }}>
            <p style={{ color: '#93c5fd', fontSize: 12, marginBottom: 8 }}>تم دفعها</p>
            <p style={{ color: '#3b82f6', fontSize: 28, fontWeight: 900 }}>{paidCommissions.toFixed(0)} AED</p>
          </div>
        </div>
      </div>
    </div>
  );
}
